/**
 * Toaster - Global toast notification stack.
 *
 * Renders the toasts held in {@link useToastStore} as a fixed top-right stack
 * sitting above the app header (z-[60] > header's z-30). Each toast is a
 * rounded card with a type-colored accent (success=green, error=red,
 * info=blue), the message, and a dismiss button.
 *
 * Accessibility:
 * - The stack wrapper is an aria-live region (polite) so new toasts are announced.
 * - success/info toasts use role="status"; error toasts use role="alert".
 * - The dismiss button is labelled "Dismiss notification".
 * - Respects prefers-reduced-motion (fade only, no slide-in).
 *
 * Mount this once at the app root.
 */

import { useEffect, useState } from 'react';
import { useToastStore, type ToastType } from '@/stores/toastStore';

// ─── Styling maps ──────────────────────────────────────────────────────────────

/** Left-border accent color per toast type. */
const ACCENT_BORDER: Record<ToastType, string> = {
  success: 'border-l-green-400',
  error: 'border-l-red-400',
  info: 'border-l-blue-400',
};

/** Icon color per toast type. */
const ICON_COLOR: Record<ToastType, string> = {
  success: 'text-green-400',
  error: 'text-red-400',
  info: 'text-blue-400',
};

/** Simple glyph per toast type (decorative). */
const ICON_GLYPH: Record<ToastType, string> = {
  success: '\u2713', // ✓
  error: '\u2715', // ✕
  info: '\u2139', // ℹ
};

// ─── Hook: prefers-reduced-motion ────────────────────────────────────────────

function usePrefersReducedMotion(): boolean {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mql.matches);
    const handler = (e: MediaQueryListEvent) =>
      setPrefersReducedMotion(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  return prefersReducedMotion;
}

// ─── Toaster ─────────────────────────────────────────────────────────────────

export function Toaster() {
  const toasts = useToastStore((s) => s.toasts);
  const removeToast = useToastStore((s) => s.removeToast);
  const prefersReducedMotion = usePrefersReducedMotion();

  return (
    <div
      aria-live="polite"
      aria-relevant="additions"
      className="fixed top-4 right-4 z-[60] flex flex-col gap-2 pointer-events-none max-w-[calc(100vw-2rem)] w-80"
    >
      {toasts.map((t) => (
        <div
          key={t.id}
          role={t.type === 'error' ? 'alert' : 'status'}
          className={`
            pointer-events-auto flex items-start gap-3
            rounded-lg border border-white/10 border-l-4 ${ACCENT_BORDER[t.type]}
            bg-bg-secondary text-text-primary shadow-card
            px-3 py-2.5
            ${prefersReducedMotion ? 'animate-fade-in' : 'animate-slide-in-right'}
          `}
        >
          {/* Accent icon */}
          <span
            className={`mt-0.5 shrink-0 text-sm font-bold ${ICON_COLOR[t.type]}`}
            aria-hidden="true"
          >
            {ICON_GLYPH[t.type]}
          </span>

          {/* Message */}
          <p className="flex-1 text-sm leading-snug break-words">{t.message}</p>

          {/* Dismiss */}
          <button
            type="button"
            onClick={() => removeToast(t.id)}
            aria-label="Dismiss notification"
            className="
              shrink-0 -mr-1 -mt-0.5 w-6 h-6 flex items-center justify-center
              rounded-md text-text-secondary hover:text-text-primary
              hover:bg-white/5 transition-colors duration-fast cursor-pointer
              focus-visible:outline-2 focus-visible:outline-focus-ring focus-visible:outline-offset-1
            "
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
      ))}
    </div>
  );
}

Toaster.displayName = 'Toaster';
